// src/modules/time-tracking/SitesList.js (admin) — manage geofenced sites.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit, FaTrash, FaMapMarkerAlt, FaRulerHorizontal, FaInbox, FaToggleOn, FaToggleOff, FaClock,
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import Pagination from '../../components/shared/Pagination';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import { StatusBadge, ResponsiveHeader } from '../../components/styled/CommonStyles';
import {
  GridContainer, Card, CardHeader, LeadName, CardContent, InfoRow,
  ActionIconsContainer, ActionButton, EmptyState,
} from '../../components/styled/GridComponents';
import { listSites, deleteSite, updateSite } from './timeTrackingService';

const SitesList = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  useEffect(() => { fetchSites(); }, []);

  const fetchSites = async () => {
    setLoading(true); setError(null);
    try {
      const { sites: data } = await listSites();
      setSites(data || []);
    } catch (e) {
      setError('Failed to load sites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // One-click on/off. This is the day-to-day control for a "clock in without a
  // job" site (the office / yard): switch it on for the crew, off when done —
  // no need to open the edit form. Optimistic, reverted if the API rejects.
  const [togglingId, setTogglingId] = useState(null);
  const handleToggleActive = async (site) => {
    const next = !site.is_active;
    setTogglingId(site.id);
    setSites((list) => list.map((s) => (s.id === site.id ? { ...s, is_active: next } : s)));
    try {
      await updateSite(site.id, { is_active: next });
    } catch (e) {
      setSites((list) => list.map((s) => (s.id === site.id ? { ...s, is_active: site.is_active } : s)));
      setFeedbackMessage('Failed to update site: ' + (e.response?.data?.message || e.message));
      setFeedbackSuccess(false); setShowFeedback(true);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await deleteSite(toDelete.id);
      setSites((list) => list.filter((s) => s.id !== toDelete.id));
      setFeedbackMessage('Site deleted successfully');
      setFeedbackSuccess(true);
    } catch (e) {
      setFeedbackMessage('Failed to delete site: ' + (e.response?.data?.message || e.message));
      setFeedbackSuccess(false);
    } finally {
      setShowFeedback(true); setLoading(false);
      setDeleteModalOpen(false); setToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return sites.filter((s) => !q || s.name?.toLowerCase().includes(q) || s.address?.toLowerCase().includes(q));
  }, [sites, searchQuery]);

  const current = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <DashboardLayout>
      <ResponsiveHeader title="Geofenced Sites" onSearch={setSearchQuery} onAdd={() => navigate('/time-tracking/sites/new')} />

      {loading && <LoadingSpinner />}
      {error && !loading && <EmptyState><p>{error}</p></EmptyState>}

      {!loading && !error && (
        <GridContainer>
          {current.length === 0 ? (
            <EmptyState>
              <FaInbox size={48} />
              <p>
                No sites yet. Add a parking lot / yard so employees can clock in there — and tick
                &ldquo;Allow clock-in without a scheduled job&rdquo; on it if you want crews to be able to
                clock in for last-minute work with no job in the system.
              </p>
            </EmptyState>
          ) : current.map((s) => (
            <Card key={s.id}>
              <CardHeader>
                <LeadName>{s.name}</LeadName>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {s.allow_standalone_clock_in && (
                    <StatusBadge color="#e0a800" title="Employees can clock in here without a scheduled job">
                      No job needed
                    </StatusBadge>
                  )}
                  <StatusBadge color={s.is_active ? '#84c959' : '#7f8c8d'}>{s.is_active ? 'Active' : 'Inactive'}</StatusBadge>
                </div>
              </CardHeader>
              <CardContent>
                <InfoRow><FaMapMarkerAlt /><span>{[s.address, s.city, s.state].filter(Boolean).join(', ') || `${s.latitude}, ${s.longitude}`}</span></InfoRow>
                <InfoRow><FaRulerHorizontal /><span>{s.radius_feet} ft radius</span></InfoRow>
                {s.allow_standalone_clock_in && (
                  <InfoRow>
                    <FaClock />
                    <span>
                      {s.is_active
                        ? 'Crews can clock in here any day, no scheduled job required.'
                        : 'Job-free clock-in is set up here, but the site is switched off.'}
                    </span>
                  </InfoRow>
                )}
              </CardContent>
              <ActionIconsContainer>
                <ActionButton
                  onClick={() => handleToggleActive(s)}
                  disabled={togglingId === s.id}
                  title={s.is_active ? 'Switch off — hides it from the clock-in list' : 'Switch on — crews can clock in here'}
                >
                  {s.is_active ? <FaToggleOn /> : <FaToggleOff />}
                </ActionButton>
                <ActionButton onClick={() => navigate(`/time-tracking/sites/edit/${s.id}`)} title="Edit site"><FaEdit /></ActionButton>
                <ActionButton onClick={() => { setToDelete(s); setDeleteModalOpen(true); }} title="Delete site"><FaTrash /></ActionButton>
              </ActionIconsContainer>
            </Card>
          ))}
        </GridContainer>
      )}

      {!loading && filtered.length > itemsPerPage && (
        <Pagination itemsPerPage={itemsPerPage} totalItems={filtered.length}
          paginate={setCurrentPage} currentPage={currentPage} />
      )}

      {deleteModalOpen && (
        <Modal title="Delete Site"
          message={`Delete site "${toDelete?.name || ''}"? Employees will no longer be able to clock in there, and past shifts recorded at this site will lose their link to it. To just take it out of rotation, use the on/off switch instead — that keeps the history intact.`}
          onConfirm={handleDelete}
          onCancel={() => { setDeleteModalOpen(false); setToDelete(null); }}
          isOpen={deleteModalOpen} />
      )}

      <FeedbackModal isOpen={showFeedback} message={feedbackMessage} success={feedbackSuccess}
        onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default SitesList;
